import { Close as CloseIcon } from "@mui/icons-material";
import { Assessment, Rocket, SocialDistance, Stars } from "@mui/icons-material";
import {
  Alert,
  Box,
  Drawer,
  IconButton,
  Paper,
  Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { SCROLL_TARGETS } from "../../common/constants";
import { useFilters } from "../../common/hooks/useFilters";
import FilterBar from "../../components/FilterBar";
import MetricCard from "../../components/MetricCard";
import { useAggregatedLensPerformance, useAttributePerformance } from "../../data/hooks/usePerformanceAnalysis";
import { CONVERSATION_LENS } from "../../utils/terminology";
import { getUserTimezone } from "../../utils/timezone";
import { useSelectedGroups } from "../auth/hooks/useSelectedGroups";
import { DRAWER_WIDTH, useLayout } from "../layout/LayoutContext";
import { Viewer } from "../viewer/Viewer";
import AttributeAnalysisTable from "./components/AttributeAnalysisTable";
import CallSamplesSection from "./components/CallSamplesSection";
import LensAnalysisTable from "./components/LensAnalysisTable";
import LensTimelineView from "./components/LensTimelineView";
import TeamMemberAnalysisTable from "./components/TeamMemberAnalysisTable";

const MIN_CALLS_THRESHOLD = 10;
const MAIN_PADDING = 24;

const PerformanceAnalysis = () => {
  const { filters, updateFilter } = useFilters({
    defaults: {
      timeRange: "last7Days"
    }
  });
  const { isDrawerOpen } = useLayout();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedInteractionId, setSelectedInteractionId] = useState<string | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState<string | undefined>(undefined);

  const teamLeaderboardRef = useRef<HTMLDivElement>(null);

  const handleUserClick = (value: string) => {
    updateFilter("user", value);
  };

  const handleLensClick = (value: string) => {
    updateFilter("protocol", value);
  };

  const handleCallClick = (interactionId: string, attribute?: string) => {
    setSelectedInteractionId(interactionId);
    setSelectedAttribute(attribute);
    setIsViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setIsViewerOpen(false);
    setSelectedAttribute(undefined);
  };

  const timezone = getUserTimezone();
  const { permittedEmails } = useSelectedGroups();

  const contentWidth = isDrawerOpen
    ? `calc(100vw - ${DRAWER_WIDTH + MAIN_PADDING * 2}px)`
    : `calc(100vw - ${MAIN_PADDING * 2}px)`;

  const {
    data: aggregatedLensPerformanceData,
    isLoading: isLoadingLenses,
    error: lensError
  } = useAggregatedLensPerformance({
    timeRange: filters.timeRange,
    timezone,
    lensFilter: filters.protocol,
    userFilter: filters.user,
    permittedEmails
  });

  const {
    data: attributePerformanceData,
    isLoading: isLoadingAttributes,
    error: attributeError
  } = useAttributePerformance({
    timeRange: filters.timeRange,
    timezone,
    lensFilter: filters.protocol || "all",
    userFilter: filters.user,
    permittedEmails,
    enabled: !!filters.protocol && filters.protocol !== "all"
  });

  const metricData = filters.user === "all"
    ? aggregatedLensPerformanceData?.all
    : aggregatedLensPerformanceData?.byUser?.find(d => d.user_email === filters.user);

  const lensData = filters.user === "all"
    ? aggregatedLensPerformanceData?.byLens
    : aggregatedLensPerformanceData?.byLensAndUser;

  const showLeaderboard = filters.user === "all";

  useEffect(() => {
    const shouldScrollToTeamLeaderBoard = searchParams.get("scroll_to") === SCROLL_TARGETS.TEAM_LEADERBOARD && !isLoadingLenses && showLeaderboard;

    if (shouldScrollToTeamLeaderBoard && teamLeaderboardRef.current) {
      teamLeaderboardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      searchParams.delete("scroll_to");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, isLoadingLenses, showLeaderboard]);

  const totalScoredCount = (metricData?.scored_count || 0) + (metricData?.not_scored_count || 0);
  const pctScored = totalScoredCount > 0 ? ((metricData?.scored_count || 0) / totalScoredCount * 100).toFixed(0) : "0";

  const attributeTitle = filters.user === "all" ? "Aggregated Team Performance by Attribute" : "Performance by Attribute";

  const showInsufficientDataWarning = filters.user !== "all"
    && (metricData?.scored_count || 0) < MIN_CALLS_THRESHOLD;

  return (
    <Box sx={{ p: 1, width: contentWidth }}>
      <FilterBar
        title="Performance Analysis"
        subTitle={`Analyze performance metrics by team member and ${CONVERSATION_LENS}`}
        timeRange={filters.timeRange}
        sentiment={filters.sentiment}
        relevance={filters.relevance}
        risk={filters.risk}
        protocol={filters.protocol}
        user={filters.user}
        processDecision={filters.processDecision}
        callType={filters.callType}
        onFilterChange={updateFilter}
        showFilters={{
          timeRange: true,
          protocol: true,
          user: true,
          sentiment: false,
          relevance: false,
          risk: false,
          processDecision: false,
          callType: false,
          scoreRange: false,
          search: false
        }}
        sticky={true}
      />

      {showInsufficientDataWarning && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This user has {metricData?.scored_count || 0} scored calls. At least {MIN_CALLS_THRESHOLD} calls are needed for best performer comparisons, so this user may have a higher average than the current best performer.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 4 }}>
        {/*
          Cards:
          - Total Calls Scored (trend vs previous)
          - Average Score (trend vs previous)
          - Score Range (min, max) - Sublabel standard dev
          - Top Performer Avg - sublabel (email address)
          - Vs Best Performer Avg - sublabel (%)
        */}
        <Grid size={{ xs: 12, sm: 6, md: "grow" }}>
          <MetricCard
            title="Total Calls Scored"
            icon={<Assessment />}
            value={`${metricData?.scored_count} / ${totalScoredCount}`}
            sublabel={`${pctScored}% scored · ${metricData?.not_scored_count} not scored`}
            isLoading={isLoadingLenses}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: "grow" }}>
          <MetricCard
            title="Average Score (0-5)"
            icon={<Stars />}
            value={`${metricData?.avg_score?.toFixed(2) || "0.00"}`}
            trend={metricData?.pct_delta_vs_prev_avg}
            sublabel={`Median ${metricData?.median_score?.toFixed(2) || "0.00"} · Range ${metricData?.min_score?.toFixed(2) || "0.00"} - ${metricData?.max_score?.toFixed(2) || "0.00"} · σ ${metricData?.stddev_score?.toFixed(2) || "0.00"}`}
            isLoading={isLoadingLenses}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: "grow" }}>
          <MetricCard
            title="Best Performer"
            icon={<Rocket />}
            value={`${metricData?.best_user_avg_score?.toFixed(2) || "0.00"}`}
            sublabel={`${metricData?.best_user_email || "N/A"}`}
            isLoading={isLoadingLenses}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: "grow" }}>
          <MetricCard
            title="Gap to Best"
            icon={<SocialDistance />}
            value={`${metricData?.delta_vs_best?.toFixed(2) || "0.00"} (${metricData?.pct_delta_vs_best?.toFixed(2) || "0.00"}%)`}
            sublabel={`${metricData?.best_user_email || "N/A"}`}
            isLoading={isLoadingLenses}
          />
        </Grid>

      </Grid>

      <LensAnalysisTable
        data={lensData || []}
        isLoading={isLoadingLenses}
        error={lensError}
        userFilter={filters.user}
        lensFilter={filters.protocol}
        permittedEmails={permittedEmails}
        onClick={handleLensClick}
      />

      {showLeaderboard && (
        <Box ref={teamLeaderboardRef}>
          <TeamMemberAnalysisTable
            data={aggregatedLensPerformanceData?.byUser || []}
            isLoading={isLoadingLenses}
            error={lensError}
            userFilter={filters.user}
            lensFilter={filters.protocol}
            permittedEmails={permittedEmails}
            onClick={handleUserClick}
          />
        </Box>
      )}

      {/* Lens Timeline View */}
      {filters.protocol && filters.protocol !== "all" && (
        <LensTimelineView
          lensName={filters.protocol}
          timeRange={filters.timeRange}
          timezone={timezone}
          userFilter={filters.user}
          permittedEmails={permittedEmails}
          groupAvgScore={metricData?.group_avg_score || 0}
          groupBestAvgScore={metricData?.best_user_avg_score || 0}
        />
      )}

      {/* Attribute Analysis Section - positioned at the bottom */}
      {filters.protocol && filters.protocol !== "all" ? (
        <AttributeAnalysisTable
          title={attributeTitle}
          data={filters.user === "all" ? attributePerformanceData?.all || [] : attributePerformanceData?.byUser || []}
          isLoading={isLoadingAttributes}
          error={attributeError}
          userFilter={filters.user}
          lensName={filters.protocol}
        />
      ) : (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Attribute Performance</Typography>
          <Typography variant="body2" color="text.secondary">
            Select a conversation lens to see the related attribute results here.
          </Typography>
        </Paper>
      )}

      {/* Call Samples Section */}
      {filters.user !== "all" && filters.protocol && filters.protocol !== "all" ? (
        <CallSamplesSection
          timeRange={filters.timeRange}
          timezone={timezone}
          protocol={filters.protocol}
          user={filters.user}
          permittedEmails={permittedEmails}
          onCallClick={handleCallClick}
        />
      ) : (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Call Samples</Typography>
          <Typography variant="body2" color="text.secondary">
            Select a user and a lens to see their top 5, bottom 5, and sample call scores here.
          </Typography>
        </Paper>
      )}

      {/* Call Viewer Drawer */}
      <Drawer
        anchor="right"
        open={isViewerOpen}
        onClose={handleCloseViewer}
        sx={{
          width: "75%",
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: "75%",
            boxSizing: "border-box",
            zIndex: (theme) => theme.zIndex.drawer + 2,
            transition: "transform 0.3s ease-in-out"
          }
        }}
        transitionDuration={300}
      >
        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          pt: 3,
          mt: 7,
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
        }}>
          <Typography variant="h6" component="div">
            Call Details
          </Typography>
          <Box>
            <IconButton
              onClick={handleCloseViewer}
              aria-label="close panel"
              color="primary"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        {selectedInteractionId && (
          <Box sx={{ height: "calc(100% - 70px)", overflow: "auto" }}>
            <Viewer
              interactionId={selectedInteractionId}
              initialTab={selectedAttribute ? 1 : 0}
              expandedAttribute={selectedAttribute}
            />
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default PerformanceAnalysis;
